<script>
import { Avatar, StandardSecurityProfiles } from '@artup/model'
import { FormMixin } from '@artup/client'

export default {
  mixins: [
    FormMixin
  ],

  props: {
    // Dialog visibility, use with `v-model`
    modelValue: {
      type: Boolean,
      default: false
    },
    // Avatar to edit, a new one is created if not specified
    avatar: {
      default: null
    },
    // Security profiles to choose from
    securityProfiles: {
      type: Array,
      default: () => []
    },
    // Allow changing the confidential settings: security profile and enabled flag.
    // Only administrators editing somebody else's account may do so
    confidential: {
      type: Boolean,
      default: false
    },
    // Number of avatars the account has, the default flag can only be changed when there is a choice
    avatarCount: {
      type: Number,
      default: 0
    },
    // Indicates that the editor is a super-user editing somebody else's account,
    // the only situation in which the super-user profile may be assigned
    superUser: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'update:modelValue',
    'ok',
    'cancel'
  ],

  data () {
    return {
      // Edited copy of the avatar
      data: new Avatar()
    }
  },

  computed: {
    // Dialog visibility
    isOpen: {
      get () {
        return this.modelValue
      },
      set (value) {
        this.$emit('update:modelValue', value)
      }
    },

    // Dialog title
    title () {
      return this.avatar?.id ? 'Edit Avatar' : 'New Avatar'
    },

    // Security profiles which the editor may assign: without confidential access only
    // the profiles users may pick for themselves, the super-user profile only for super-users,
    // and never the integration profile, as integration accounts are managed manually
    assignableProfiles () {
      const { securityProfiles, confidential, superUser } = this
      return securityProfiles.filter(profile => {
        if (profile.name === StandardSecurityProfiles.Integration) return false
        if (profile.name === StandardSecurityProfiles.Super) return superUser
        return confidential || !profile.isInternal
      })
    },

    // Security profiles as selector options
    profileOptions () {
      return this.assignableProfiles
        .map(profile => ({
          value: profile.name,
          label: profile.description || profile.name,
          isHuman: profile.isHuman
        }))
    },

    // The security profile of an existing avatar can only be changed with confidential access
    canChangeProfile () {
      return this.confidential || !this.avatar?.id
    },

    // Indicates that this is the only avatar of the account, once the editor is confirmed
    isOnlyAvatar () {
      const { avatar, avatarCount } = this
      return avatar ? avatarCount <= 1 : avatarCount === 0
    },

    // Selected security profile
    securityProfile () {
      return this.securityProfiles.find(profile => profile.name === this.data.securityProfileName)
    },

    // Indicates that the selected profile represents a human, so first and last name apply
    isHuman () {
      return this.securityProfile ? this.securityProfile.isHuman !== false : true
    }
  },

  methods: {
    // Triggered when the dialog is shown, prepares the edited copy
    shown () {
      const { avatar } = this
      // New avatars start as regular users, unless that profile cannot be assigned
      const { assignableProfiles } = this
      const defaultProfile = assignableProfiles.find(profile => profile.name === StandardSecurityProfiles.User) || assignableProfiles[0]
      this.data = new Avatar({
        isEnabled: true,
        isDefault: false,
        securityProfileName: defaultProfile?.name,
        ...(avatar ? JSON.parse(JSON.stringify(avatar)) : {})
      })
      // The only avatar is always the default one
      if (this.isOnlyAvatar) {
        this.data.isDefault = true
      }
    },

    // Confirms the changes
    async ok () {
      if (!await this.validate()) return

      const { data, isHuman, securityProfile } = this
      // Display name of the avatar is derived from its details
      const personName = [data.firstName, data.lastName]
        .filter(Boolean)
        .join(' ')
        .trim()
      data.name = (isHuman ? personName : data.organization) || personName || securityProfile?.description || data.securityProfileName

      this.$emit('ok', data)
      this.isOpen = false
    },

    // Discards the changes
    cancel () {
      this.$emit('cancel')
      this.isOpen = false
    }
  }
}
</script>

<template>
  <q-dialog v-model="isOpen" persistent @show="shown()">
    <q-card class="avatar-editor">
      <q-banner class="banner bg-indigo-6 text-white">
        <span class="title">
          {{ title }}
        </span>
      </q-banner>

      <q-form ref="form" class="form column q-gutter-md q-pa-lg">
        <q-select label="Security profile" outlined v-model="data.securityProfileName" :options="profileOptions"
          emit-value map-options :rules="[rules.required]" :readonly="!canChangeProfile"
          :hint="canChangeProfile ? '' : 'Only administrators can change the security profile of an existing avatar'">
        </q-select>

        <template v-if="isHuman">
          <q-input label="First name" outlined v-model="data.firstName" maxlength="255">
          </q-input>
          <q-input label="Last name" outlined v-model="data.lastName" maxlength="255">
          </q-input>
        </template>

        <q-input label="Organization" outlined v-model="data.organization" maxlength="255"
          :rules="isHuman ? [] : [rules.required]">
        </q-input>

        <div class="row q-gutter-lg">
          <q-toggle v-if="confidential" label="Enabled" v-model="data.isEnabled" color="indigo-6">
          </q-toggle>
          <q-toggle label="Default avatar" v-model="data.isDefault" color="indigo-6" :disable="isOnlyAvatar">
            <q-tooltip v-if="isOnlyAvatar">
              The only avatar of an account is always the default one
            </q-tooltip>
          </q-toggle>
        </div>
      </q-form>

      <footer class="row justify-between q-pa-lg">
        <q-btn label="Cancel" color="grey-6" unelevated no-caps class="button" @click="cancel()">
        </q-btn>
        <q-btn label="OK" color="indigo-6" unelevated no-caps class="button" @click="ok()">
        </q-btn>
      </footer>
    </q-card>
  </q-dialog>
</template>

<style lang="scss" scoped>
.avatar-editor {
  width: 520px;
  max-width: 90vw;

  .banner .title {
    font-size: 18px;
  }

  .button {
    min-width: 110px;
  }
}
</style>
